import { createBroadsignVideoHandler } from '../../../MediaController';
import { BroadsignVideoHandler } from '../../../MediaController/Video/Broadsign';
import { ReachAPI } from '../../../ReachApi';
import { getPlayerUuid } from '../../../Signage';
import { glom } from '../../../Signage/glom';
import { WidgetType } from '../../types';
import AdQueue from '../AdQueue';
import { BaseThirdPartyAdWidget } from '../BaseThirdPartyAdWidget';
import { PlayerTokenError } from '../errors';
import { BroadsignApiHelper } from './BroadsignApiHelper';
import {
    BroadsignAd,
    BroadsignWidgetConfiguration,
    BroadsignWidgetConfigurationDetails,
    BroadsignWidgetTimelineSlot,
    ZodBroadsignWidgetConfigurationDetails,
} from './types';

export class BroadsignWidget extends BaseThirdPartyAdWidget {
    type: WidgetType.BROADSIGN;
    declare config: BroadsignWidgetConfigurationDetails;

    protected readonly adQueue: AdQueue<BroadsignAd> = new AdQueue();

    constructor(widgetConfiguration: BroadsignWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.type = WidgetType.BROADSIGN;
    }

    protected validateConfigurationDetails(
        broadsignWidgetConfigurationDetails: BroadsignWidgetConfigurationDetails
    ): BroadsignWidgetConfigurationDetails {
        return ZodBroadsignWidgetConfigurationDetails.parse(broadsignWidgetConfigurationDetails);
    }
    /**
     * Attempts to send proof of play
     * for current ad if necessary.
     * @throws {@link PlayerTokenError} when token player token
     * or player UUID id aren't set.
     */
    async afterFadeOut(currentSlot: BroadsignWidgetTimelineSlot): Promise<void> {
        if (!currentSlot.params.ad.isFallback && currentSlot.params.ad.playcount >= 1) {
            if (!glom.playerUuid && !glom.securityToken) {
                throw new PlayerTokenError('Player uuid or security token must be set');
            }

            const security = new ReachAPI.Security(
                glom.playerUuid || undefined,
                glom.securityToken || undefined
            );

            await BroadsignApiHelper.sendProofOfPlay(
                currentSlot.params.ad,
                getPlayerUuid(),
                security,
                this.id
            );
        }
        await super.afterFadeOut(currentSlot);
    }

    /**
     * Fetches ads from Broadsign ad serving API.
     * @throws {@link PlayerTokenError} when token player token
     * or player UUID id aren't set.
     */
    async getAds(): Promise<void> {
        if (!glom.playerUuid && !glom.securityToken) {
            throw new PlayerTokenError('Player uuid or security token must be set');
        }

        const security = new ReachAPI.Security(
            glom.playerUuid || undefined,
            glom.securityToken || undefined
        );

        await BroadsignApiHelper.fetchAds(
            security,
            this.id,
            this.numberOfAds,
            this.adHeight,
            this.adWidth,
            this.durationInSeconds,
            this.muteAds,
            this.adQueue,
            getPlayerUuid()
        );
    }

    getThirdPartyAdVendorVideoHandler(assetUrl: string): BroadsignVideoHandler {
        return createBroadsignVideoHandler({
            videoUrl: assetUrl,
            muted: this.muteAds,
        });
    }
}
